import { useCallback } from "react";

import { DeliveryMethod } from "../../constants";
import useOrderFormik from "../useOrderFormik";

type TClearConflictingDataParams = {
  prefillLockerCountryCode?: string;
  prefillAddressCountryCode?: string;
  fieldNames: {
    addressId?: string;
    country: string;
    deliveryMethod: string;
    name: string;
    phoneNumber: string;
    email: string;
    street: string;
    houseNo: string;
    flatNo: string;
    city: string;
    zipCode: string;
    locker: string;
    lockerName: string;
    pickupDate: string;
    pickupTimeFrom: string;
    pickupTimeTill: string;
  };
  prefill: {
    addressId?: string;
    name?: string;
    phoneNumber?: string;
    email?: string;
    street?: string;
    houseNo?: string;
    flatNo?: string;
    city?: string;
    zipCode?: string;
    locker?: string;
    lockerName?: string;
  };
  addressDeliveryMethods: DeliveryMethod[];
  lockerDeliveryMethods: DeliveryMethod[];
  fromLockerDeliveryMethods: DeliveryMethod[];
};

type TClearConflictingDataReturn = {
  handleCountryChange: (countryCode: string) => void;
  handleDeliveryMethodChange: (deliveryMethod: DeliveryMethod) => void;
};

const useClearConflictingData = ({
  prefillLockerCountryCode,
  prefillAddressCountryCode,
  fieldNames,
  prefill,
  addressDeliveryMethods,
  lockerDeliveryMethods,
  fromLockerDeliveryMethods,
}: TClearConflictingDataParams): TClearConflictingDataReturn => {
  // Using setFieldValue instead of setValues, because this callback is going to be used in onChange
  // When using setValues inside onChange callback, it will overwrite new selected value with old one
  const { values, setFieldValue } = useOrderFormik();

  const name = values[fieldNames.name];
  const phoneNumber = values[fieldNames.phoneNumber];
  const email = values[fieldNames.email];
  const locker = values[fieldNames.locker];
  const lockerName = values[fieldNames.lockerName];
  const street = values[fieldNames.street];
  const houseNo = values[fieldNames.houseNo];
  const flatNo = values[fieldNames.flatNo];
  const city = values[fieldNames.city];
  const zipCode = values[fieldNames.zipCode];

  const selectedCountry = values[fieldNames.country] as string;
  const selectedDeliveryMethod = values[fieldNames.deliveryMethod] as DeliveryMethod;

  const hasEmptyAddressFields = !name && !phoneNumber && !email && !street && !houseNo && !flatNo && !city && !zipCode;

  const hasEmptyLockerFields = !name && !phoneNumber && !email && !locker && !lockerName;

  const prefillAddressFields = useCallback(() => {
    if (fieldNames.addressId) {
      setFieldValue(fieldNames.addressId, prefill.addressId);
    }
    setFieldValue(fieldNames.name, prefill.name);
    setFieldValue(fieldNames.phoneNumber, prefill.phoneNumber);
    setFieldValue(fieldNames.email, prefill.email);
    setFieldValue(fieldNames.street, prefill.street);
    setFieldValue(fieldNames.houseNo, prefill.houseNo);
    setFieldValue(fieldNames.flatNo, prefill.flatNo);
    setFieldValue(fieldNames.city, prefill.city);
    setFieldValue(fieldNames.zipCode, prefill.zipCode);

    setFieldValue(fieldNames.locker, "");
    setFieldValue(fieldNames.lockerName, "");
  }, [
    fieldNames.addressId,
    fieldNames.city,
    fieldNames.email,
    fieldNames.flatNo,
    fieldNames.houseNo,
    fieldNames.locker,
    fieldNames.lockerName,
    fieldNames.name,
    fieldNames.phoneNumber,
    fieldNames.street,
    fieldNames.zipCode,
    prefill.addressId,
    prefill.city,
    prefill.email,
    prefill.flatNo,
    prefill.houseNo,
    prefill.name,
    prefill.phoneNumber,
    prefill.street,
    prefill.zipCode,
    setFieldValue,
  ]);

  const prefillLockerFields = useCallback(() => {
    if (fieldNames.addressId) {
      setFieldValue(fieldNames.addressId, prefill.addressId);
    }
    setFieldValue(fieldNames.name, prefill.name);
    setFieldValue(fieldNames.phoneNumber, prefill.phoneNumber);
    setFieldValue(fieldNames.email, prefill.email);
    setFieldValue(fieldNames.locker, prefill.locker);
    setFieldValue(fieldNames.lockerName, prefill.lockerName);
    setFieldValue(fieldNames.street, "");
    setFieldValue(fieldNames.houseNo, "");
    setFieldValue(fieldNames.flatNo, "");
    setFieldValue(fieldNames.city, "");
    setFieldValue(fieldNames.zipCode, "");
  }, [
    fieldNames.addressId,
    fieldNames.city,
    fieldNames.email,
    fieldNames.flatNo,
    fieldNames.houseNo,
    fieldNames.locker,
    fieldNames.lockerName,
    fieldNames.name,
    fieldNames.phoneNumber,
    fieldNames.street,
    fieldNames.zipCode,
    prefill.addressId,
    prefill.email,
    prefill.locker,
    prefill.lockerName,
    prefill.name,
    prefill.phoneNumber,
    setFieldValue,
  ]);

  // Clearing only address and locker fields. Name, email and phone can be left
  const clearLockerFields = useCallback(() => {
    setFieldValue(fieldNames.locker, "");
    setFieldValue(fieldNames.lockerName, "");
  }, [fieldNames.locker, fieldNames.lockerName, setFieldValue]);

  const clearAddressFields = useCallback(() => {
    setFieldValue(fieldNames.street, "");
    setFieldValue(fieldNames.houseNo, "");
    setFieldValue(fieldNames.flatNo, "");
    setFieldValue(fieldNames.city, "");
    setFieldValue(fieldNames.zipCode, "");
  }, [fieldNames.city, fieldNames.flatNo, fieldNames.houseNo, fieldNames.street, fieldNames.zipCode, setFieldValue]);

  const clearPickupTimeFields = useCallback(() => {
    setFieldValue(fieldNames.pickupDate, "");
    setFieldValue(fieldNames.pickupTimeFrom, "");
    setFieldValue(fieldNames.pickupTimeTill, "");
  }, [fieldNames.pickupDate, fieldNames.pickupTimeFrom, fieldNames.pickupTimeTill, setFieldValue]);

  const clearAddressIdField = useCallback(() => {
    if (fieldNames.addressId) {
      setFieldValue(fieldNames.addressId, null);
    }
  }, [fieldNames.addressId, setFieldValue]);

  const handleFieldsChange = useCallback(
    (countryCode: string, deliveryMethod: DeliveryMethod) => {
      if (fromLockerDeliveryMethods.includes(deliveryMethod)) {
        clearPickupTimeFields();
      }

      // Change to prefill address country. Fill prefill address info if current fields are empty
      if (
        countryCode === prefillAddressCountryCode &&
        hasEmptyAddressFields &&
        addressDeliveryMethods.includes(deliveryMethod)
      ) {
        prefillAddressFields();
        return;
      }

      // Change to prefill locker country. Fill prefill locker info if current fields are empty
      if (
        countryCode === prefillLockerCountryCode &&
        hasEmptyLockerFields &&
        lockerDeliveryMethods.includes(deliveryMethod)
      ) {
        prefillLockerFields();
        return;
      }

      if (countryCode !== prefillAddressCountryCode) {
        clearAddressFields();
      }

      if (countryCode !== prefillLockerCountryCode) {
        clearLockerFields();
      }

      if (countryCode !== prefillAddressCountryCode && countryCode !== prefillLockerCountryCode) {
        clearAddressIdField();
      }
    },
    [
      prefillAddressCountryCode,
      hasEmptyAddressFields,
      addressDeliveryMethods,
      fromLockerDeliveryMethods,
      prefillLockerCountryCode,
      hasEmptyLockerFields,
      lockerDeliveryMethods,
      prefillAddressFields,
      prefillLockerFields,
      clearPickupTimeFields,
      clearAddressFields,
      clearLockerFields,
      clearAddressIdField,
    ]
  );

  // This should only be called when user explicitly changes country
  const handleCountryChange = useCallback(
    (countryCode) => {
      handleFieldsChange(countryCode, selectedDeliveryMethod);
    },
    [handleFieldsChange, selectedDeliveryMethod]
  );

  // This should only be called when user explicitly changes delivery method
  const handleDeliveryMethodChange = useCallback(
    (method: DeliveryMethod) => {
      handleFieldsChange(selectedCountry, method);
    },
    [handleFieldsChange, selectedCountry]
  );

  return { handleCountryChange, handleDeliveryMethodChange };
};
export default useClearConflictingData;
